import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  Condo,
  CondoContact,
  CondoRule,
  SaveContactRequest,
  SaveRuleRequest,
  UpdateCondoRequest,
} from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'

export const condoQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.condo(condoId),
    queryFn: ({ signal }) =>
      api.get<Condo>('/condominiums/:id', { params: { id: condoId }, signal }),
  })

function useCondoMutation<V>(condoId: number, fn: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.condo(condoId) }),
  })
}

/** PATCH accepts only email, website and history; name, address, phone and CNPJ stay locked. */
export const useUpdateCondo = (condoId: number) =>
  useCondoMutation(condoId, (body: UpdateCondoRequest) =>
    api.patch<Condo>('/condominiums/:id', body, { params: { id: condoId } }),
  )

export const useSaveRule = (condoId: number) =>
  useCondoMutation(condoId, ({ id, ...body }: SaveRuleRequest & { id?: number }) =>
    id === undefined
      ? api.post<CondoRule>('/condominiums/:id/rules', body, { params: { id: condoId } })
      : api.patch<CondoRule>('/condominiums/:id/rules/:ruleId', body, {
          params: { id: condoId, ruleId: id },
        }),
  )

export const useDeleteRule = (condoId: number) =>
  useCondoMutation(condoId, (ruleId: number) =>
    api.delete('/condominiums/:id/rules/:ruleId', { params: { id: condoId, ruleId } }),
  )

export const useSaveContact = (condoId: number) =>
  useCondoMutation(condoId, ({ id, ...body }: SaveContactRequest & { id?: number }) =>
    id === undefined
      ? api.post<CondoContact>('/condominiums/:id/contacts', body, { params: { id: condoId } })
      : api.patch<CondoContact>('/condominiums/:id/contacts/:contactId', body, {
          params: { id: condoId, contactId: id },
        }),
  )

export const useDeleteContact = (condoId: number) =>
  useCondoMutation(condoId, (contactId: number) =>
    api.delete('/condominiums/:id/contacts/:contactId', { params: { id: condoId, contactId } }),
  )
